import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  BADGE_POSITIONS,
  CARD_LAYOUTS,
  CARD_RANGES,
  CardColors,
  CardLayout,
  CardStyle,
  DEFAULT_CARD_STYLE,
  LIGHT_CARD_COLORS,
  NAME_POSITIONS,
  TIME_POSITIONS,
  saveCardStyle,
} from "../../../../service/BuyerConfiguration/BuyerAppDashboard/restaurantCardsService";
import { ColorField, SliderRow, pct } from "../ThemeStudio/fields";
import { GREEN, GREEN_DARK, deepEqual, errMsg } from "../ThemeStudio/themeUtils";
import { CardPreview, SAMPLE_CARDS } from "./CardPreview";

export const btnGreen = { bgcolor: GREEN, "&:hover": { bgcolor: GREEN_DARK }, textTransform: "none", borderRadius: 2 } as const;
export const btnOutline = { textTransform: "none", borderColor: GREEN, color: GREEN_DARK, borderRadius: 2 } as const;

export const LAYOUT_LABELS: Record<CardLayout, string> = {
  overlay: "Overlay",
  split: "Split",
  classic: "Classic",
};

export const POSITION_LABELS: Record<string, string> = {
  "top-left": "Top left",
  "center-left": "Centre left",
  "bottom-left": "Bottom left",
  "top-right": "Top right",
  "bottom-right": "Bottom right",
  hidden: "Hidden",
};

const COLOR_FIELDS: Array<{ key: keyof CardColors; label: string }> = [
  { key: "name", label: "Name" },
  { key: "meta", label: "Details text" },
  { key: "ratingBg", label: "Rating pill" },
  { key: "chipBg", label: "Chip background" },
  { key: "chipText", label: "Chip text" },
  { key: "timeBg", label: "Time pill" },
  { key: "timeText", label: "Time text" },
];

/** Small drawing of each layout for the layout picker. */
export const LayoutThumb: React.FC<{ layout: CardLayout }> = ({ layout }) => {
  const photo = "linear-gradient(135deg, #6b3a1f, #c4541c)";
  const line = (w: number, c = "#fff") => <Box sx={{ height: 3, width: w, bgcolor: c, borderRadius: 1, mb: "3px" }} />;
  if (layout === "classic") {
    return (
      <Box sx={{ width: 72, height: 46, borderRadius: 1.5, overflow: "hidden", border: "1px solid rgba(0,0,0,0.12)", bgcolor: "#fff" }}>
        <Box sx={{ height: 26, background: photo }} />
        <Box sx={{ p: "4px 6px" }}>
          {line(34, "#333")}
          {line(22, "#999")}
        </Box>
      </Box>
    );
  }
  if (layout === "split") {
    return (
      <Box sx={{ width: 72, height: 46, borderRadius: 1.5, overflow: "hidden", border: "1px solid rgba(0,0,0,0.12)", position: "relative", bgcolor: "#FFF6EE" }}>
        <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 38, background: photo }} />
        <Box sx={{ position: "absolute", left: 30, top: -6, bottom: -6, width: 12, borderRadius: "50%", bgcolor: "#FFF6EE", borderRight: "2px solid #F26A1B" }} />
        <Box sx={{ position: "absolute", left: 6, top: 14 }}>
          {line(24, "#333")}
          {line(16, "#999")}
        </Box>
      </Box>
    );
  }
  return (
    <Box sx={{ width: 72, height: 46, borderRadius: 1.5, overflow: "hidden", border: "1px solid rgba(0,0,0,0.12)", position: "relative", background: photo }}>
      <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(90deg, rgba(0,0,0,0.7), transparent 75%)" }} />
      <Box sx={{ position: "absolute", left: 6, top: 15 }}>
        {line(28)}
        {line(18, "rgba(255,255,255,0.7)")}
      </Box>
    </Box>
  );
};

export const LayoutPicker: React.FC<{
  value: CardLayout | undefined;
  onChange: (v: CardLayout) => void;
  /** Adds a "Use global" choice (value undefined). */
  onUseGlobal?: () => void;
}> = ({ value, onChange, onUseGlobal }) => (
  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
    {onUseGlobal && (
      <Box
        role="button"
        tabIndex={0}
        onClick={onUseGlobal}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onUseGlobal()}
        sx={{
          p: 1,
          borderRadius: 2,
          cursor: "pointer",
          border: `2px solid ${value === undefined ? GREEN : "rgba(0,0,0,0.1)"}`,
          minWidth: 88,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>Use global</Typography>
      </Box>
    )}
    {CARD_LAYOUTS.map((l) => (
      <Box
        key={l}
        role="button"
        tabIndex={0}
        aria-pressed={value === l}
        onClick={() => onChange(l)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onChange(l)}
        sx={{
          p: 1,
          borderRadius: 2,
          cursor: "pointer",
          border: `2px solid ${value === l ? GREEN : "rgba(0,0,0,0.1)"}`,
          textAlign: "center",
          "&:hover": { borderColor: value === l ? GREEN : "rgba(0,0,0,0.3)" },
        }}
      >
        <LayoutThumb layout={l} />
        <Typography sx={{ fontSize: 12, fontWeight: 600, mt: 0.5 }}>{LAYOUT_LABELS[l]}</Typography>
      </Box>
    ))}
  </Stack>
);

export const Section: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <Card sx={{ mb: 2, borderRadius: 3, boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
    <CardContent>
      <Typography sx={{ fontWeight: 700, fontSize: 15 }}>{title}</Typography>
      {hint && <Typography sx={{ color: "text.secondary", fontSize: 12.5, mt: 0.25 }}>{hint}</Typography>}
      <Stack spacing={2} sx={{ mt: 1.5 }}>
        {children}
      </Stack>
    </CardContent>
  </Card>
);

export const OverlayToggle: React.FC<{ value: "dark" | "light" | undefined; onChange: (v: "dark" | "light") => void }> = ({
  value,
  onChange,
}) => (
  <ToggleButtonGroup
    exclusive
    size="small"
    value={value ?? null}
    onChange={(_, v) => v && onChange(v)}
    sx={{ "& .Mui-selected": { bgcolor: "rgba(0,177,79,0.12) !important", color: `${GREEN_DARK} !important` }, "& .MuiToggleButton-root": { textTransform: "none", px: 2 } }}
  >
    <ToggleButton value="dark">Dark fade</ToggleButton>
    <ToggleButton value="light">Light fade</ToggleButton>
  </ToggleButtonGroup>
);

export const CardLookTab: React.FC<{
  saved: CardStyle;
  onSaved: (s: CardStyle) => void;
  onToast: (msg: string, ok: boolean) => void;
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ saved, onSaved, onToast, onDirtyChange }) => {
  const [draft, setDraft] = React.useState<CardStyle>(saved);
  const [saving, setSaving] = React.useState(false);
  React.useEffect(() => setDraft(saved), [saved]);

  const dirty = !deepEqual(draft, saved);
  React.useEffect(() => onDirtyChange?.(dirty), [dirty, onDirtyChange]);

  const set = <K extends keyof CardStyle>(k: K, v: CardStyle[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setPos = (k: keyof CardStyle["positions"], v: any) =>
    setDraft((d) => ({ ...d, positions: { ...d.positions, [k]: v } }));
  const setColor = (k: keyof CardColors, v: string) => setDraft((d) => ({ ...d, colors: { ...d.colors, [k]: v } }));

  const setOverlay = (overlay: "dark" | "light") =>
    setDraft((d) => {
      if (d.overlay === overlay) return d;
      // Swap to the matching text colours if they are still the other preset's.
      const from = d.overlay === "light" ? LIGHT_CARD_COLORS : DEFAULT_CARD_STYLE.colors;
      const to = overlay === "light" ? LIGHT_CARD_COLORS : DEFAULT_CARD_STYLE.colors;
      return { ...d, overlay, colors: deepEqual(d.colors, from) ? to : d.colors };
    });

  const save = async () => {
    setSaving(true);
    try {
      const s = await saveCardStyle(draft);
      onSaved(s);
      setDraft(s);
      onToast("Saved. Apps update in a few seconds.", true);
    } catch (e) {
      onToast(errMsg(e, "Could not save the card look. Try again."), false);
    } finally {
      setSaving(false);
    }
  };

  const textOnPanel = draft.layout !== "overlay" && draft.overlay === "dark";

  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">
      <Box sx={{ position: { md: "sticky" }, top: { md: 16 }, flexShrink: 0 }}>
        <Box sx={{ bgcolor: "#F4F5F7", borderRadius: 4, p: 2, width: 392, maxWidth: "100%", boxSizing: "border-box" }}>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1.5 }}>
            Live preview with sample restaurants
          </Typography>
          <Stack spacing={2} alignItems="center">
            {SAMPLE_CARDS.map((c) => (
              <CardPreview key={c.name} style={draft} data={c} />
            ))}
          </Stack>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center" flexWrap="wrap" useFlexGap>
          <Button variant="contained" onClick={save} disabled={saving || !dirty} sx={btnGreen}>
            {saving ? "Saving…" : "Save look"}
          </Button>
          <Button variant="outlined" onClick={() => setDraft(saved)} disabled={saving || !dirty} sx={btnOutline}>
            Discard changes
          </Button>
          <Button
            startIcon={<RestartAltIcon />}
            onClick={() => {
              setDraft(DEFAULT_CARD_STYLE);
              onToast("Reset to the default look. Save to apply it.", true);
            }}
            disabled={saving || deepEqual(draft, DEFAULT_CARD_STYLE)}
            sx={{ textTransform: "none", color: GREEN_DARK }}
          >
            Reset to default
          </Button>
          {dirty && <Typography sx={{ fontSize: 12.5, color: "#B26A00" }}>Unsaved changes</Typography>}
        </Stack>

        <Section title="Layout" hint="How the photo and the details sit on every card.">
          <LayoutPicker value={draft.layout} onChange={(v) => set("layout", v)} />
        </Section>

        <Section title="Photo fade" hint="A fade from the left over the photo keeps the text readable.">
          <OverlayToggle value={draft.overlay} onChange={setOverlay} />
          <SliderRow
            label="Fade strength"
            {...CARD_RANGES.overlayStrength}
            value={draft.overlayStrength}
            onChange={(v) => set("overlayStrength", v)}
            format={pct}
          />
        </Section>

        <Section title="Size and frame">
          <SliderRow label="Card height" {...CARD_RANGES.height} value={draft.height} onChange={(v) => set("height", v)} format={(v) => `${v} px`} />
          <SliderRow label="Corner radius" {...CARD_RANGES.radius} value={draft.radius} onChange={(v) => set("radius", v)} format={(v) => `${v} px`} />
          <ColorField
            label="Border colour"
            value={draft.borderColor}
            onChange={(v) => set("borderColor", v)}
            onClear={() => set("borderColor", null)}
            placeholder="No border"
          />
          <SliderRow
            label="Border width"
            {...CARD_RANGES.borderWidth}
            value={draft.borderWidth}
            onChange={(v) => set("borderWidth", v)}
            format={(v) => `${v} px`}
            hint={!draft.borderColor ? "Pick a border colour to show the border." : undefined}
          />
          <FormControlLabel
            control={<Switch checked={draft.shadow} onChange={(e) => set("shadow", e.target.checked)} sx={switchSx} />}
            label={<Typography sx={{ fontSize: 13.5 }}>Drop shadow</Typography>}
          />
        </Section>

        <Section title="Positions">
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select size="small" fullWidth label="Name" value={draft.positions.name} onChange={(e) => setPos("name", e.target.value)}>
              {NAME_POSITIONS.map((p) => (
                <MenuItem key={p} value={p}>{POSITION_LABELS[p]}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" fullWidth label="Badge" value={draft.positions.badge} onChange={(e) => setPos("badge", e.target.value)}>
              {BADGE_POSITIONS.map((p) => (
                <MenuItem key={p} value={p}>{POSITION_LABELS[p]}</MenuItem>
              ))}
            </TextField>
            <TextField select size="small" fullWidth label="Delivery time" value={draft.positions.time} onChange={(e) => setPos("time", e.target.value)}>
              {TIME_POSITIONS.map((p) => (
                <MenuItem key={p} value={p}>{POSITION_LABELS[p]}</MenuItem>
              ))}
            </TextField>
          </Stack>
          <FormControlLabel
            control={<Switch checked={draft.positions.chips} onChange={(e) => setPos("chips", e.target.checked)} sx={switchSx} />}
            label={<Typography sx={{ fontSize: 13.5 }}>Show feature chips</Typography>}
          />
        </Section>

        <Section
          title="Colours"
          hint={
            textOnPanel
              ? "Split and classic cards put the text on a light panel, so they use the light-fade text colours. Switch to a light fade to edit them for these layouts."
              : "Hex (#RRGGBB) or rgba(r,g,b,a)."
          }
        >
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            {COLOR_FIELDS.map((f) => (
              <ColorField key={f.key} label={f.label} value={draft.colors[f.key]} onChange={(v) => setColor(f.key, v)} />
            ))}
          </Box>
        </Section>

        <Section title="Text">
          <SliderRow
            label="Name weight"
            {...CARD_RANGES.nameWeight}
            value={draft.nameWeight}
            onChange={(v) => set("nameWeight", v)}
            format={(v) => (v >= 800 ? `${v} (extra bold)` : v >= 700 ? `${v} (bold)` : `${v}`)}
          />
        </Section>

        <Section title="Photos" hint="Restaurants with more than one photo can roll through them.">
          <FormControlLabel
            control={
              <Switch
                checked={draft.photos.rolling}
                onChange={(e) => set("photos", { ...draft.photos, rolling: e.target.checked })}
                sx={switchSx}
              />
            }
            label={<Typography sx={{ fontSize: 13.5 }}>Roll through photos</Typography>}
          />
          {draft.photos.rolling && (
            <SliderRow
              label="Change every"
              {...CARD_RANGES.intervalSec}
              value={draft.photos.intervalSec}
              onChange={(v) => set("photos", { ...draft.photos, intervalSec: v })}
              format={(v) => `${v}s`}
            />
          )}
        </Section>
      </Box>
    </Stack>
  );
};

export const switchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: GREEN },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: GREEN },
} as const;

export default CardLookTab;
